import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FaBars,
  FaChartBar,
  FaClipboardList,
  FaEnvelope,
  FaSignOutAlt,
  FaTimes,
  FaUsers,
} from 'react-icons/fa';

const navigationItems = [
  {
    label: 'Orçamentos',
    to: '/admin/cadastrar-orcamento',
    icon: FaClipboardList,
  },
  {
    label: 'Painel de Logs',
    to: '/admin/painel-logs',
    icon: FaChartBar,
  },
  {
    label: 'Usuários',
    to: '/admin/usuarios',
    icon: FaUsers,
  },
  {
    label: 'Mensagens',
    to: '/admin/mensagens',
    icon: FaEnvelope,
  },
];

function getNavClasses({ isActive }) {
  return [
    'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors',
    isActive
      ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/20'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  ].join(' ');
}

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  function handleLogout() {
    localStorage.removeItem('userInfo');
    toast('Você saiu do painel administrativo.', { icon: '👋' });
    navigate('/login');
  }

  function openSidebar() {
    setIsSidebarOpen(true);
  }

  function closeSidebar() {
    setIsSidebarOpen(false);
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
              NoteMaster Admin
            </p>
            <p className="truncate text-sm font-medium text-gray-700">
              {userInfo?.username || 'Administrador'}
            </p>
          </div>

          <button
            type="button"
            onClick={openSidebar}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            <FaBars className="h-4 w-4" />
            <span>Menu</span>
          </button>
        </div>
      </div>

      {isSidebarOpen ? (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={closeSidebar}
          className="fixed inset-0 z-40 bg-gray-950/40 md:hidden"
        />
      ) : null}

      <div className="mx-auto flex min-h-screen max-w-7xl flex-col gap-6 p-4 md:flex-row md:p-6">
        <aside
          className={[
            'fixed inset-y-0 left-0 z-50 w-[85vw] max-w-xs overflow-y-auto rounded-r-3xl bg-white p-5 shadow-2xl transition-transform duration-200 md:sticky md:top-6 md:z-auto md:w-80 md:max-w-none md:self-start md:rounded-3xl md:shadow-sm',
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          ].join(' ')}
        >
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-5">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-600">
                NoteMaster Admin
              </p>
              <h1 className="mt-2 text-2xl font-bold text-gray-900">
                Painel administrativo
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Gerencie operações internas com navegação centralizada e acesso restrito.
              </p>
            </div>

            <button
              type="button"
              onClick={closeSidebar}
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-50 md:hidden"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-5 rounded-2xl bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
              Sessão atual
            </p>
            <p className="mt-2 text-base font-semibold text-gray-900">
              {userInfo?.username || 'Administrador'}
            </p>
            <p className="text-sm text-gray-500">
              {userInfo?.email || 'Sem email disponível'}
            </p>
          </div>

          <nav className="mt-6 flex flex-col gap-2" aria-label="Menu administrativo">
            {navigationItems.map(({ label, to, icon: Icon }) => (
              <NavLink key={to} to={to} className={getNavClasses} onClick={closeSidebar}>
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            onClick={handleLogout}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
          >
            <FaSignOutAlt className="h-4 w-4" />
            <span>Sair</span>
          </button>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
